import { supabase } from '../supabaseClient.js';
import { v4 as uuidv4 } from 'uuid';

/**
 * Servicio para manejar operaciones de planes en Supabase
 * Específicamente para campos jsonarray y jsonmails
 */

/**
 * Actualiza la configuración JSON de un plan en Supabase
 * @param {string} planId - ID del plan a actualizar
 * @param {Object} configData - Datos de configuración
 * @param {string} configData.jsonarray - JSON string para jsonarray
 * @param {string} configData.jsonmails - JSON string para jsonmails
 * @returns {Promise<Object>} - Datos actualizados del plan
 */
export const updatePlanConfiguration = async (planId, configData) => {
  try {
    console.log('🔄 Actualizando configuración del plan:', planId, configData);
    
    // Validar que planId existe
    if (!planId) {
      throw new Error('ID del plan es requerido');
    }

    // Preparar datos para actualizar
    const updateData = {};
    
    if (configData.jsonarray !== undefined) {
      updateData.jsonarray = configData.jsonarray;
    }
    
    if (configData.jsonmails !== undefined) {
      updateData.jsonmails = configData.jsonmails;
    }

    // Agregar timestamp de actualización
    updateData.updated_at = new Date().toISOString();

    console.log('📝 Datos a actualizar:', updateData);

    // Ejecutar actualización en Supabase
    const { data, error } = await supabase
      .from('planes')
      .update(updateData)
      .eq('id', planId)
      .select();

    if (error) {
      console.error('❌ Error de Supabase:', error);
      throw error;
    }

    if (!data || data.length === 0) {
      throw new Error('No se encontró el plan con el ID especificado');
    }

    console.log('✅ Configuración actualizada exitosamente:', data[0]);
    return data[0];
    
  } catch (error) {
    console.error('❌ Error actualizando configuración del plan:', error);
    throw error;
  }
};

/**
 * Obtiene la configuración JSON de un plan desde Supabase
 * @param {string} planId - ID del plan
 * @returns {Promise<Object>} - Configuración del plan
 */
export const getPlanConfiguration = async (planId) => {
  try {
    console.log('🔍 Obteniendo configuración del plan:', planId);
    
    if (!planId) {
      throw new Error('ID del plan es requerido');
    }

    const { data, error } = await supabase
      .from('planes')
      .select('id, jsonarray, jsonmails, updated_at')
      .eq('id', planId)
      .single();

    if (error) {
      console.error('❌ Error obteniendo configuración:', error);
      throw error;
    }

    console.log('✅ Configuración obtenida:', data);
    return data;
    
  } catch (error) {
    console.error('❌ Error obteniendo configuración del plan:', error);
    throw error;
  }
};

/**
 * Obtiene los precios base de un plan desde Supabase
 * @param {string} planId - ID del plan
 * @returns {Promise<Array>} - Array de precios base
 */
export const getPreciosBase = async (planId) => {
  try {
    console.log('🔍 Obteniendo precios base del plan:', planId);
    
    if (!planId) {
      throw new Error('ID del plan es requerido');
    }

    const { data, error } = await supabase
      .from('precios_base')
      .select('*')
      .eq('plan_id', planId)
      .eq('Activo', true)
      .order('created_at', { ascending: true });

    if (error) {
      console.error('❌ Error obteniendo precios base:', error);
      throw error;
    }

    console.log('✅ Precios base obtenidos:', data);
    return data || [];
    
  } catch (error) {
    console.error('❌ Error obteniendo precios base del plan:', error);
    throw error;
  }
};

/**
 * Actualiza los precios base de un plan en Supabase
 * @param {string} planId - ID del plan
 * @param {Array} preciosBase - Array de precios base
 * @returns {Promise<Array>} - Array de precios base actualizados
 */
// Método: updatePreciosBase
export const updatePreciosBase = async (planId, preciosBase) => {
  try {
    console.log('🔄 Actualizando precios base del plan:', planId, preciosBase);
    if (!planId) {
      throw new Error('ID del plan es requerido');
    }
    if (!Array.isArray(preciosBase)) {
      throw new Error('preciosBase debe ser un array');
    }

    // Preparar datos para upsert (evita borrar filas referenciadas por certificados)
    const preciosToUpsert = preciosBase.map(precio => {
      const row = {
        plan_id: planId,
        tramo_etario: precio.tramo_etario || null,
        sexo: precio.sexo || null,
        actividad_id: precio.actividad_id || null,
        capital_id: precio.capital_id || null,
        deducible_id: precio.deducible_id || null,
        precio_uf: precio.precio_uf ? parseFloat(precio.precio_uf) : null,
        prevision: precio.prevision || null,
        producto_id: precio.producto_id || null,
        edad_minima: precio.edad_minima ? parseInt(precio.edad_minima) : null,
        edad_maxima: precio.edad_maxima ? parseInt(precio.edad_maxima) : null,
        Activo: precio.Activo !== undefined ? precio.Activo : true,
        tasa: precio.tasa ? parseFloat(precio.tasa) : null,
        monto_minimo: precio.monto_minimo ? parseFloat(precio.monto_minimo) : null,
        monto_maximo: precio.monto_maximo ? parseFloat(precio.monto_maximo) : null,
        moneda: precio.moneda || 'UF',
        // Programa de cobro: fuente canónica que leen flow/payment.
        // n_cuotas NULL = derivar de frecuencia (mensual=12, anual=1).
        frecuencia: precio.frecuencia || 'mensual',
        n_cuotas: precio.n_cuotas ? parseInt(precio.n_cuotas, 10) : null,
        poliza_colectiva_id: precio.poliza_colectiva_id || null,
        updated_at: new Date().toISOString(),
      };
      // Incluir id solo si es UUID real (string). Evita IDs locales numéricos tipo Date.now().
      if (typeof precio.id === 'string') {
        row.id = precio.id;
      }
      return row;
    });

    if (preciosToUpsert.length === 0) {
      console.log('✅ Sin cambios en precios base (array vacío)');
      return [];
    }

    console.log('📝 Precios a upsert:', preciosToUpsert);

    const { data, error } = await supabase
      .from('precios_base')
      .upsert(preciosToUpsert, { onConflict: 'id' })
      .select();

    if (error) {
      console.error('❌ Error upsert precios base:', error);
      throw error;
    }

    console.log('✅ Precios base actualizados exitosamente:', data);
    return data;
  } catch (error) {
    console.error('❌ Error actualizando precios base del plan:', error);
    throw error;
  }
};

export const getPreciosCarga = async (planId) => {
  try {
    console.log('🔍 Obteniendo precios de cargas del plan:', planId);
    if (!planId) {
      throw new Error('ID del plan es requerido');
    }

    const { data, error } = await supabase
      .from('precios_carga')
      .select('*')
      .eq('plan_id', planId)
      .order('created_at', { ascending: true });

    if (error) {
      console.error('❌ Error obteniendo precios de cargas:', error);
      throw error;
    }

    console.log('✅ Precios de cargas obtenidos:', data);
    return data || [];
  } catch (error) {
    console.error('❌ Error obteniendo precios de cargas del plan:', error);
    throw error;
  }
};

export const updatePreciosCarga = async (planId, preciosCarga) => {
  try {
    console.log('🔄 Actualizando precios de cargas del plan:', planId, preciosCarga);
    if (!planId) {
      throw new Error('ID del plan es requerido');
    }
    if (!Array.isArray(preciosCarga)) {
      throw new Error('preciosCarga debe ser un array');
    }

    const preciosValidos = preciosCarga.filter(
      (precio) => typeof precio?.precio_base_id === 'string'
    );

    const preciosToUpsert = preciosValidos.map((precio) => {
      const resolvedId = typeof precio?.id === 'string' && precio.id ? precio.id : uuidv4();
      const row = {
        plan_id: planId,
        precio_base_id: precio.precio_base_id,
        tipo_carga: precio.tipo_carga || null,
        precio_uf: precio.precio_uf ? parseFloat(precio.precio_uf) : null,
        moneda: precio.moneda || 'UF',
        activo: precio.activo !== undefined ? precio.activo : true,
        updated_at: new Date().toISOString(),
        id: resolvedId,
      };
      return row;
    });

    const { data: existing, error: existingError } = await supabase
      .from('precios_carga')
      .select('id')
      .eq('plan_id', planId);

    if (existingError) {
      console.error('❌ Error obteniendo precios de cargas existentes:', existingError);
      throw existingError;
    }

    const idsToKeep = preciosToUpsert
      .map((precio) => precio.id)
      .filter((id) => typeof id === 'string');

    const idsToDelete = (existing || [])
      .map((row) => row.id)
      .filter((id) => !idsToKeep.includes(id));

    if (idsToDelete.length > 0) {
      const { error: deleteError } = await supabase
        .from('precios_carga')
        .delete()
        .in('id', idsToDelete);

      if (deleteError) {
        console.error('❌ Error eliminando precios de cargas:', deleteError);
        throw deleteError;
      }
    }

    if (preciosToUpsert.length === 0) {
      console.log('✅ Sin cambios en precios de cargas (array vacío)');
      return [];
    }

    const { data, error } = await supabase
      .from('precios_carga')
      .upsert(preciosToUpsert, { onConflict: 'id' })
      .select();

    if (error) {
      console.error('❌ Error upsert precios de cargas:', error);
      throw error;
    }

    console.log('✅ Precios de cargas actualizados exitosamente:', data);
    return data;
  } catch (error) {
    console.error('❌ Error actualizando precios de cargas del plan:', error);
    throw error;
  }
};

export const updateCoberturaJson = async (planId, coberturaJson) => {
  try {
    if (!planId) throw new Error('ID del plan es requerido');
    const now = new Date().toISOString();

    const { data, error } = await supabase
      .from('planes')
      .update({ json_coberturas: coberturaJson, updated_at: now })
      .eq('id', planId)
      .select('id, json_coberturas');

    if (!error) {
      console.log('✅ json_coberturas guardado para plan:', data?.[0]?.id);
      return data?.[0];
    }

    console.error('❌ Error guardando json_coberturas:', error);
    throw error;
  } catch (error) {
    console.error('❌ Error en updateCoberturaJson:', error);
    throw error;
  }
};

export const assignPolizaToPrecios = async (precioIds, polizaId) => {
  try {
    if (!polizaId || !Array.isArray(precioIds) || precioIds.length === 0) {
      return [];
    }
    const { data, error } = await supabase
      .from('precios_base')
      .update({ poliza_colectiva_id: polizaId, updated_at: new Date().toISOString() })
      .in('id', precioIds)
      .select();

    if (error) throw error;
    return data || [];
  } catch (error) {
    console.error('❌ Error asignando póliza a tramos:', error);
    throw error;
  }
};

export const clearPolizaFromPrecios = async (precioIds) => {
  try {
    if (!Array.isArray(precioIds) || precioIds.length === 0) {
      return [];
    }
    const { data, error } = await supabase
      .from('precios_base')
      .update({ poliza_colectiva_id: null, updated_at: new Date().toISOString() })
      .in('id', precioIds)
      .select();

    if (error) throw error;
    return data || [];
  } catch (error) {
    console.error('❌ Error desasignando póliza de tramos:', error);
    throw error;
  }
};
